import { ref } from 'vue';
import api from '@/services/api';

export function useImageProcessing() {
    const conversionHistory = ref([]);
    const showVerifyModal = ref(false);
    const selectedItem = ref(null);
    const molecule = ref(null);
    const processingBatch = ref(false);

    let verifyResolver = null;

    const fetchIUPACName = async (identifier, type) => {
        try {
            const response = await fetch(
                `https://cactus.nci.nih.gov/chemical/structure/${encodeURIComponent(identifier)}/iupac_name`
            );
            if (!response.ok) return null;
            const name = await response.text();
            return name.trim();
        } catch (error) {
            console.error(`Failed to fetch IUPAC from ${type}:`, error);
            return null;
        }
    };

    const saveCanvasToThumbnail = (thumbnail) => {
        conversionHistory.value.push({
            thumbnail,
            latex: null,
            processing: false,
            timestamp: Date.now(),
            chemicalData: null
        });
    };

    const deleteThumbnail = (index) => {
        conversionHistory.value.splice(index, 1);
    };

    const openVerifyModal = (item, index) => {
        return new Promise((resolve) => {
            verifyResolver = resolve;
            selectedItem.value = { ...item, index };
            // molecule.value is already set before calling this function
            showVerifyModal.value = true;
        });
    };

    const keepAndSubmit = () => {
        showVerifyModal.value = false;
        if (verifyResolver) {
            verifyResolver('keep');
        }
    };

    const redrawMolecule = () => {
        const index = selectedItem.value?.index;
        if (index !== undefined) {
            deleteThumbnail(index);
        }
        showVerifyModal.value = false;
        if (verifyResolver) {
            verifyResolver('redraw');
        }
    };

    const cancelVerification = () => {
        showVerifyModal.value = false;
        if (verifyResolver) {
            verifyResolver('cancel');
        }
    };

    const insertLatexFromHistory = (latex, userAnswer) => {
        if (!latex || latex === '[Conversion failed]') return userAnswer.value;

        if (userAnswer.value) {
            userAnswer.value += '\n' + latex;
        } else {
            userAnswer.value = latex;
        }
        return userAnswer.value;
    };

    const convertAllThumbnails = async () => {
        processingBatch.value = true;

        for (let i = 0; i < conversionHistory.value.length; i++) {
            const item = conversionHistory.value[i];

            if (item.latex) continue;

            item.processing = true;

            try {
                // Create canvas from thumbnail
                const img = new Image();
                await new Promise((resolve) => {
                    img.onload = resolve;
                    img.src = item.thumbnail;
                });

                const off = document.createElement('canvas');
                off.width = img.width;
                off.height = img.height;
                const offCtx = off.getContext('2d');
                offCtx.drawImage(img, 0, 0);

                // Invert colors for Mathpix
                const imageData = offCtx.getImageData(0, 0, off.width, off.height);
                const data = imageData.data;
                for (let j = 0; j < data.length; j += 4) {
                    data[j] = 255 - data[j];
                    data[j + 1] = 255 - data[j + 1];
                    data[j + 2] = 255 - data[j + 2];
                }
                offCtx.putImageData(imageData, 0, 0);

                // Convert to blob and send to API
                const blob = await new Promise((resolve) => {
                    off.toBlob(resolve, 'image/webp', 0.95);
                });

                const formData = new FormData();
                formData.append('image', blob, 'chem-drawing.webp');

                const response = await api.processImage(formData);
                const result = JSON.parse(response.data);

                item.latex = result.latex_styled;

                // Extract SMILES and InChI from the raw text
                const rawText = result.text || '';

                const smilesMatch = rawText.match(/>([^<]+)<\/smiles>/);
                const smiles = smilesMatch?.[1] || null;

                const inchiMatch = rawText.match(/inchi="([^"]+)"/);
                const inchi = inchiMatch?.[1] || null;

                const inchiKeyMatch = rawText.match(/inchi_key="([^"]+)"/);
                const inchiKey = inchiKeyMatch?.[1] || null;

                molecule.value = smiles || null;

                const decision = await openVerifyModal(item, i);

                if (decision === 'redraw') {
                    item.processing = false;
                    processingBatch.value = false;
                    return { redrawRequested: true };
                }

                if (decision === 'cancel') {
                    // Reset processing state for current item
                    item.processing = false;
                    // Reset any remaining items
                    for (let j = i; j < conversionHistory.value.length; j++) {
                        conversionHistory.value[j].processing = false;
                    }
                    processingBatch.value = false;
                    molecule.value = null;
                    return { cancelled: true };
                }

                // Fetch IUPAC names in parallel
                const [iupacFromSmiles, iupacFromInchi] = await Promise.all([
                    smiles ? fetchIUPACName(smiles, 'SMILES') : Promise.resolve(null),
                    inchi ? fetchIUPACName(inchi, 'InChI') : Promise.resolve(null),
                ]);

                // Store structured chemical data
                item.chemicalData = {
                    smiles,
                    inchi,
                    inchiKey,
                    iupacFromSmiles,
                    iupacFromInchi,
                };
            } catch (error) {
                console.error('Error processing thumbnail:', error);
                const status = error.response?.status;
                if (status === 413) {
                    item.latex = '[Image too large — max 5MB]';
                } else if (status === 429) {
                    item.latex = '[Rate limit reached — please slow down]';
                } else {
                    item.latex = '[Conversion failed]';
                }
                item.chemicalData = null;
            } finally {
                item.processing = false;
            }
        }

        processingBatch.value = false;
        return { redrawRequested: false };
    };

    return {
        conversionHistory,
        showVerifyModal,
        selectedItem,
        molecule,
        processingBatch,
        convertAllThumbnails,
        saveCanvasToThumbnail,
        deleteThumbnail,
        openVerifyModal,
        keepAndSubmit,
        redrawMolecule,
        cancelVerification,
        insertLatexFromHistory
    };
}